/**
 * User Portal API Service
 * Handles communication with the User Portal backend API
 *
 * Pattern aligned with admin-console-service.ts for consistency.
 */

import axios from 'axios';
import { handleUnauthorized } from '../common/auth-utils';
import { getTabId } from '../common/tab-id';
import { buildTenantPath, getTenantFromPath } from '../common/tenant-utils';
import { getAppVersion } from '../config/env-config';
import {
  AgentApprovalsService,
  AgentsService,
  ApplicationsService,
  ConnectedProvidersService,
  DevicePairingService,
  DevicesService,
  DocsService,
  EscalationService,
  FactorsService,
  FavoritesService,
  HealthService,
  IdpSessionsService,
  PasswordService,
  SamlService,
  SessionService,
  UserService,
  OpenAPI,
} from './user-portal-api-generated';

// Get tenant-aware API URL for regular API calls using tenant_slug from URL path
const getUserPortalApiUrl = (): string => {
  const tenantSlug = getTenantFromPath();

  if (!tenantSlug) {
    // Return a placeholder - API calls will fail but won't cause redirects during page load
    return '/user-portal-api-no-tenant';
  }

  return `/${tenantSlug}/user-portal-api`;
};

// Get auth-specific API URL using tenant slug from URL path or provided slug
// Used for SAML endpoints that need tenant-aware URLs
const getAuthApiUrl = (slug?: string): string => {
  const tenant = slug ?? getTenantFromPath();

  if (!tenant) {
    // Return a placeholder for auth calls when no tenant in path
    return '/user-portal-api-no-tenant';
  }

  return buildTenantPath(tenant, '/user-portal-api');
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: getUserPortalApiUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Function to update OpenAPI base URL with current tenant
// Accepts optional tenant slug for use in loaders where window.location
// hasn't been updated yet (React Router runs loaders before committing navigation)
export const updateApiBaseUrl = (tenantSlug?: string): void => {
  const newBaseUrl = tenantSlug
    ? `/${tenantSlug}/user-portal-api`
    : getUserPortalApiUrl();
  OpenAPI.BASE = newBaseUrl;

  // Also update the axios instance
  if (apiClient) {
    apiClient.defaults.baseURL = newBaseUrl;
  }
};

// Create a SAML service that uses slug-based URLs
const createSamlService = () => {
  return {
    initSamlAuth: async (slug?: string) => {
      // Temporarily update base URL to use slug-based path for SAML init
      const originalBase = OpenAPI.BASE;
      OpenAPI.BASE = getAuthApiUrl(slug);

      try {
        const result = await SamlService.initSamlAuth();

        return result;
      } finally {
        // Restore original base URL
        OpenAPI.BASE = originalBase;
      }
    },
    samlLogout: async (slug?: string) => {
      // Temporarily update base URL to use slug-based path for SAML logout
      const originalBase = OpenAPI.BASE;
      OpenAPI.BASE = getAuthApiUrl(slug);

      try {
        const result = await SamlService.samlLogout();

        return result;
      } finally {
        // Restore original base URL
        OpenAPI.BASE = originalBase;
      }
    },
  };
};

// Create the custom SAML service instance
const CustomSamlService = createSamlService();

// Initial configuration
updateApiBaseUrl();
OpenAPI.WITH_CREDENTIALS = true;
OpenAPI.CREDENTIALS = 'include';
// Configure OpenAPI to include tab ID header for request correlation
OpenAPI.HEADERS = async () => ({
  'x-tab-id': getTabId(),
  'x-client-version': getAppVersion(),
});

// Request interceptor for auth token and tab ID
apiClient.interceptors.request.use(
  config => {
    // Add tab ID header for request correlation
    config.headers['x-tab-id'] = getTabId();
    config.headers['x-client-version'] = getAppVersion();

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      handleUnauthorized();
    }

    return Promise.reject(error);
  }
);

// Export services
export {
  AgentApprovalsService,
  AgentsService,
  ApplicationsService,
  ConnectedProvidersService,
  DevicePairingService,
  DevicesService,
  DocsService,
  EscalationService,
  FactorsService,
  FavoritesService,
  HealthService,
  IdpSessionsService,
  PasswordService,
  SessionService,
  UserService,
};

// Export custom SAML service that handles slug-based URLs
export { CustomSamlService as SamlService };

// Export base URL getter for use in application launch
export { getUserPortalApiUrl as getApiBaseUrl };

// Export configured axios instance
export default apiClient;
